/**
 * /admin/broadcasts — past broadcast runs audit log + compose form.
 *
 * Two surfaces:
 *   1. Audit list of past broadcasts (subject · audience · sent/failed
 *      counts · timestamps).
 *   2. Compose-and-send form (toggled with a "New broadcast" button)
 *      that calls /api/admin/broadcast/preview → confirm → send.
 *
 * Safety rails on the compose form mirror the shell script:
 *   • You MUST hit "Preview audience" first. The Send button stays
 *     disabled until a preview returns > 0 recipients.
 *   • You MUST type the literal word "SEND" (uppercase) into a
 *     confirmation field before the Send button activates.
 *   • broadcast_id is generated client-side as a uuid v4 so two
 *     send clicks (even on different tabs) won't double-send —
 *     the backend dedupes on broadcast_id.
 *
 * A pre-baked draft (Feb 27, 2026 "three new flows" email) loads in
 * one click via /api/admin/broadcasts/drafts/feb27_three_new_flows.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Send,
  RefreshCw,
  AlertTriangle,
  Users,
  Plus,
  FileText,
  Eye,
  X,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import AdminShell from "../components/AdminShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const AUDIENCE_LABELS = {
  paying: "Paying",
  marketing_opt_in: "Opt-ins",
  all_users: "All users",
  beta_cohort: "Beta cohort",
};

// RFC4122-ish v4 uuid generator. We don't need cryptographic
// uniqueness — the backend dedupes on (broadcast_id) but uses it
// only for idempotency. Math.random is good enough here.
const newBroadcastId = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const SummaryStat = ({ label, value, testid }) => (
  <div
    className="bg-white/[0.04] border border-white/10 rounded-2xl p-4"
    data-testid={testid}
  >
    <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
      {label}
    </div>
    <div className="text-3xl font-light text-white tabular-nums">{value}</div>
  </div>
);

// ─── Compose form ─────────────────────────────────────────────────
const ComposeForm = ({ onClose, onSent }) => {
  const [audience, setAudience] = useState("marketing_opt_in");
  const [contentFormat, setContentFormat] = useState("plain_text");
  const [subject, setSubject] = useState("");
  const [preheader, setPreheader] = useState("");
  const [html, setHtml] = useState("");
  const [plainText, setPlainText] = useState("");
  const [broadcastId] = useState(() => newBroadcastId());

  const [previewData, setPreviewData] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");
  const [loadingDraft, setLoadingDraft] = useState(false);

  const handleLoadDraft = async (draftId) => {
    setLoadingDraft(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/broadcasts/drafts/${draftId}`,
        { withCredentials: true },
      );
      setAudience(data.audience || "marketing_opt_in");
      setSubject(data.subject || "");
      setPreheader(data.preheader || "");
      setHtml(data.html || "");
      setPlainText(data.plain_text || "");
      setContentFormat(data.content_format || "html");
      // Reset the preview state so the user can't send without re-previewing
      // a draft they've just loaded — the audience may differ from what
      // they previewed before.
      setPreviewData(null);
      setConfirmInput("");
      toast.success("Draft loaded — review and preview before sending.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load that draft.");
    } finally {
      setLoadingDraft(false);
    }
  };

  const payload = () => ({
    audience,
    subject: subject.trim(),
    content_format: contentFormat,
    preheader:
      contentFormat === "html" ? preheader.trim() || undefined : undefined,
    html: contentFormat === "html" ? html : undefined,
    plain_text:
      contentFormat === "plain_text"
        ? plainText
        : plainText.trim() || undefined,
    broadcast_id: broadcastId,
  });

  const canPreview =
    subject.trim().length >= 3 &&
    (contentFormat === "plain_text" ? plainText : html).trim().length >= 10 &&
    !previewing;

  const handlePreview = async () => {
    if (!canPreview) return;
    setPreviewing(true);
    setPreviewData(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/broadcast/preview`,
        payload(),
        { withCredentials: true },
      );
      setPreviewData(data);
      if (!data.recipient_count) {
        toast.warning(
          "Preview returned 0 recipients — check the audience filter.",
        );
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail) ||
          "Preview failed. Check the backend logs.",
      );
    } finally {
      setPreviewing(false);
    }
  };

  const sendIsArmed =
    previewData &&
    previewData.recipient_count > 0 &&
    confirmInput === "SEND" &&
    !sending;

  const handleSend = async () => {
    if (!sendIsArmed) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/broadcast/send`,
        payload(),
        { withCredentials: true },
      );
      const success = (data.failed_count || 0) === 0;
      if (success) {
        toast.success(`Sent to ${data.sent_count} recipient(s) 💜`);
      } else {
        toast.warning(
          `Sent: ${data.sent_count} · Failed: ${data.failed_count}. Check logs.`,
        );
      }
      onSent?.();
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail) ||
          "Send failed — broadcast NOT delivered.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 space-y-5"
      data-testid="admin-broadcast-compose"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-light text-xl">New broadcast</h2>
          <p className="text-xs text-white/40 mt-1 font-mono">
            id: {broadcastId}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-full hover:bg-white/5 text-white/60 hover:text-white"
          data-testid="admin-broadcast-compose-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Pre-baked drafts */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-white/60">Load a draft:</span>
        <button
          type="button"
          onClick={() => handleLoadDraft("feb27_three_new_flows")}
          disabled={loadingDraft}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4B483]/15 hover:bg-[#D4B483]/25 border border-[#D4B483]/30 text-xs text-[#D4B483] transition-colors disabled:opacity-50"
          data-testid="admin-broadcast-load-feb27"
        >
          <FileText className="h-3 w-3" />
          Feb 27 · "3 new ways to use TruBeauté"
        </button>
      </div>

      {/* Audience */}
      <div>
        <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">
          Audience
        </label>
        <select
          value={audience}
          onChange={(e) => {
            setAudience(e.target.value);
            setPreviewData(null);
            setConfirmInput("");
          }}
          className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#D4B483]/40"
          data-testid="admin-broadcast-audience"
        >
          <option value="marketing_opt_in">
            Marketing opt-ins only (recommended)
          </option>
          <option value="paying">Paying subscribers</option>
          <option value="all_users">All users (excluding opt-outs)</option>
          <option value="beta_cohort">Beta cohort</option>
        </select>
      </div>

      <div>
        <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">
          Email format
        </label>
        <div className="grid grid-cols-2 gap-2">
          {[
            ["plain_text", "Plain text"],
            ["html", "HTML"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                setContentFormat(value);
                setPreviewData(null);
                setConfirmInput("");
              }}
              className={`px-4 py-2.5 rounded-xl border text-sm transition-colors ${
                contentFormat === value
                  ? "bg-[#D4B483]/20 border-[#D4B483]/50 text-[#D4B483]"
                  : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10"
              }`}
              data-testid={`admin-broadcast-format-${value}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">
          Subject
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={200}
          placeholder="3 new ways to use TruBeauté to find your beautiful!"
          className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#D4B483]/40 placeholder-white/30"
          data-testid="admin-broadcast-subject"
        />
      </div>

      {contentFormat === "html" && <div>
        <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">
          Preheader (Gmail snippet, optional)
        </label>
        <input
          type="text"
          value={preheader}
          onChange={(e) => setPreheader(e.target.value)}
          maxLength={150}
          placeholder="Recreate a TikTok look, drill techniques, and..."
          className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#D4B483]/40 placeholder-white/30"
          data-testid="admin-broadcast-preheader"
        />
      </div>}

      {contentFormat === "html" && <div>
        <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">
          HTML body
        </label>
        <textarea
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          rows={10}
          placeholder='<div style="...">Hi gorgeous, ...</div>'
          className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white font-mono text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#D4B483]/40 placeholder-white/30 resize-y"
          data-testid="admin-broadcast-html"
        />
        <p className="text-[11px] text-white/40 mt-1.5">
          The unsubscribe footer is appended automatically by the backend.
          Don't include one here.
        </p>
      </div>}

      <div>
        <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">
          {contentFormat === "plain_text"
            ? "Email body"
            : "Plain-text fallback (optional — auto-generated from HTML if blank)"}
        </label>
        <textarea
          value={plainText}
          onChange={(e) => setPlainText(e.target.value)}
          rows={contentFormat === "plain_text" ? 10 : 4}
          placeholder="Hi gorgeous, when I built TruBeauté last month..."
          className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:ring-2 focus:ring-[#D4B483]/40 placeholder-white/30 resize-y"
          data-testid="admin-broadcast-plaintext"
        />
        {contentFormat === "plain_text" && (
          <p className="text-[11px] text-white/40 mt-1.5">
            This will be sent as plain text only. The unsubscribe footer is
            appended automatically.
          </p>
        )}
      </div>

      {/* Preview action */}
      <div className="pt-2 border-t border-white/5">
        <button
          type="button"
          onClick={handlePreview}
          disabled={!canPreview}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4B483]/15 hover:bg-[#D4B483]/25 border border-[#D4B483]/30 text-sm text-[#D4B483] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="admin-broadcast-preview-btn"
        >
          <Eye className="h-4 w-4" />
          {previewing ? "Previewing…" : "Preview audience"}
        </button>
      </div>

      {/* Preview result */}
      {previewData && (
        <div
          className="bg-[#D4B483]/10 border border-[#D4B483]/30 rounded-2xl p-4"
          data-testid="admin-broadcast-preview-result"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#D4B483] mb-1">
                {AUDIENCE_LABELS[previewData.audience] || previewData.audience}
              </div>
              <div className="text-3xl font-light text-white tabular-nums">
                {previewData.recipient_count}
              </div>
              <div className="text-xs text-white/60 mt-0.5">
                {previewData.recipient_count === 1
                  ? "recipient"
                  : "recipients"}
              </div>
            </div>
            {previewData.sample_emails?.length > 0 && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">
                  Sample
                </div>
                <ul className="text-xs text-white/70 space-y-0.5">
                  {previewData.sample_emails.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm + send */}
      {previewData && previewData.recipient_count > 0 && (
        <div className="pt-2 border-t border-white/5 space-y-3">
          <label className="block text-xs uppercase tracking-[0.2em] text-white/60">
            Type <span className="text-[#FF9999] font-bold">SEND</span> to
            arm the send button
          </label>
          <input
            type="text"
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            placeholder="SEND"
            className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white tracking-wide focus:outline-none focus:ring-2 focus:ring-[#FF9999]/40 placeholder-white/30 font-mono"
            data-testid="admin-broadcast-confirm"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!sendIsArmed}
            className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-medium transition-all ${
              sendIsArmed
                ? "bg-[#FF9999] hover:bg-[#FF8080] text-[#2C2123]"
                : "bg-white/5 text-white/30 cursor-not-allowed"
            }`}
            data-testid="admin-broadcast-send-btn"
          >
            {sending ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Sending to {previewData.recipient_count}…
              </>
            ) : sendIsArmed ? (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Send to {previewData.recipient_count} now
              </>
            ) : (
              "Type SEND above to enable"
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default function AdminBroadcasts() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCompose, setShowCompose] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/broadcasts`, {
        withCredentials: true,
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError("unauthorized");
      else if (status === 403) setError("forbidden");
      else setError("network");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalSent = items.reduce((s, b) => s + (b.sent_count || 0), 0);
  const totalFailed = items.reduce((s, b) => s + (b.failed_count || 0), 0);
  const lastSentIso = items[0]?.completed_at || null;
  const lastSentLabel = lastSentIso ? formatDate(lastSentIso) : "—";

  return (
    <AdminShell user={user} title="Broadcasts" testid="admin-broadcasts-page">
      <div className="space-y-6" data-testid="admin-broadcasts-root">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryStat
            label="Broadcasts sent"
            value={items.length}
            testid="admin-broadcasts-stat-count"
          />
          <SummaryStat
            label="Total recipients"
            value={totalSent.toLocaleString()}
            testid="admin-broadcasts-stat-sent"
          />
          <SummaryStat
            label="Failures (lifetime)"
            value={totalFailed}
            testid="admin-broadcasts-stat-failed"
          />
          <SummaryStat
            label="Last send"
            value={lastSentLabel}
            testid="admin-broadcasts-stat-last"
          />
        </div>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="text-sm text-white/60 max-w-2xl leading-relaxed">
            Compose a broadcast in the form below, or audit past sends.
            Every email auto-appends an unsubscribe footer and a
            "you signed up at trubeaute.com" privacy note. Idempotent
            on broadcast_id — accidental double-sends are not possible.
          </div>
          <div className="flex items-center gap-2">
            {!showCompose && (
              <button
                type="button"
                onClick={() => setShowCompose(true)}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#D4B483] hover:bg-[#C4A473] text-[#2C2123] text-sm font-medium transition-colors"
                data-testid="admin-broadcast-new"
              >
                <Plus className="h-4 w-4" />
                New broadcast
              </button>
            )}
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/80 transition-colors"
              data-testid="admin-broadcasts-refresh"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
        </div>

        {showCompose && (
          <ComposeForm
            onClose={() => setShowCompose(false)}
            onSent={load}
          />
        )}

        {error ? (
          <div
            className="bg-[#3a1f1f] border border-[#7a3030] rounded-2xl p-5 text-sm text-[#FFB5B5] flex items-center gap-3"
            data-testid="admin-broadcasts-error"
          >
            <AlertTriangle className="h-5 w-5 shrink-0" />
            {error === "forbidden"
              ? "Forbidden — sign in with an admin account."
              : error === "unauthorized"
              ? "Session expired. Please sign in again."
              : "Network error. Try refreshing."}
          </div>
        ) : loading ? (
          <div className="space-y-3" data-testid="admin-broadcasts-loading">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-24 bg-white/[0.03] border border-white/5 rounded-2xl animate-pulse"
              />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div
            className="text-center py-20 text-white/40 border border-dashed border-white/10 rounded-2xl"
            data-testid="admin-broadcasts-empty"
          >
            <Send className="h-8 w-8 mx-auto mb-3 text-white/30" />
            <p className="text-sm">No broadcasts sent yet.</p>
            <p className="text-xs mt-1">
              Click <strong>New broadcast</strong> above to compose your first.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((b) => (
              <div
                key={b.broadcast_id}
                className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 hover:bg-white/[0.06] transition-colors"
                data-testid={`admin-broadcast-row-${b.broadcast_id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-[#D4B483] uppercase tracking-[0.22em] mb-1">
                      {AUDIENCE_LABELS[b.audience] || b.audience}
                    </div>
                    <h3 className="text-white font-light text-lg leading-snug">
                      {b.subject}
                    </h3>
                    {b.preheader && (
                      <p className="text-xs text-white/40 mt-1.5 italic line-clamp-1">
                        {b.preheader}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="inline-flex items-center gap-1.5 text-sm text-white">
                      <Users className="h-3.5 w-3.5 text-[#D4B483]" />
                      <span className="tabular-nums font-medium">
                        {b.sent_count}
                      </span>
                      <span className="text-white/40">/</span>
                      <span className="tabular-nums text-white/60">
                        {b.sent_count + b.failed_count}
                      </span>
                    </div>
                    {b.failed_count > 0 && (
                      <div className="text-xs text-[#FFB5B5] mt-1">
                        {b.failed_count} failed
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-white/40 flex-wrap">
                  <span>{formatDate(b.completed_at)}</span>
                  <span className="text-white/20">·</span>
                  <code className="text-white/50 bg-white/5 px-1.5 py-0.5 rounded text-[11px]">
                    {b.broadcast_id}
                  </code>
                  {b.sender_name && (
                    <>
                      <span className="text-white/20">·</span>
                      <span>from {b.sender_name}</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
